import React, { useContext, useEffect, useCallback, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import { registerGrid, unregisterGrid, loadGridData, clearRefresh } from '../state/gridActions';
import { getQueryParams, doesNeedRefresh } from '../state/gridSelectors';

const GridContext = React.createContext({ name: '', isLoading: false, error: null, retry: null });

/**
 * @returns {string} grid name (legacy callers)
 */
export const useGridContext = () => {
	const context = useContext(GridContext);
	return context.name;
};

/**
 * Full grid context including optional request lifecycle (when trackRequestLifecycle).
 * @returns {{ name: string, isLoading: boolean, error: Error|null, retry: Function|null }}
 */
export const useGridRequestState = () => useContext(GridContext);

const updateGridData =
	(name, loadData, onDataReceived, shouldCommit = () => true) =>
	async (dispatch, getState) => {
		const state = getState();
		const response = await dispatch(loadData(getQueryParams(state, name)));
		if (!shouldCommit()) {
			return response;
		}
		dispatch(onDataReceived(response));
		dispatch(loadGridData(name, response));
		return response;
	};

const BaseGridProvider = ({
	name,
	primaryKey,
	loadData,
	onDataReceived,
	manual,
	retainOnUnmount,
	trackRequestLifecycle,
	children,
}) => {
	const dispatch = useDispatch();
	const needsUpdate = useSelector((state) => doesNeedRefresh(state, name));
	const [isLoading, setIsLoading] = useState(!manual && trackRequestLifecycle);
	const [error, setError] = useState(null);
	const requestSeqRef = useRef(0);
	const mountedRef = useRef(true);
	const getNewDataRef = useRef();

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	getNewDataRef.current = async () => {
		if (trackRequestLifecycle) {
			const seq = ++requestSeqRef.current;
			if (mountedRef.current) {
				setIsLoading(true);
				setError(null);
			}
			try {
				await dispatch(
					updateGridData(
						name,
						loadData,
						onDataReceived,
						() => mountedRef.current && seq === requestSeqRef.current
					)
				);
				if (!mountedRef.current || seq !== requestSeqRef.current) {
					return;
				}
				setIsLoading(false);
			} catch (err) {
				if (!mountedRef.current || seq !== requestSeqRef.current) {
					return;
				}
				setIsLoading(false);
				setError(err || new Error('Failed to load grid data'));
			}
			return;
		}
		dispatch(updateGridData(name, loadData, onDataReceived));
	};

	const retry = useCallback(() => {
		if (!trackRequestLifecycle) {
			return;
		}
		getNewDataRef.current();
	}, [trackRequestLifecycle]);

	const debouncedGetNewData = useMemo(
		() => debounce(() => getNewDataRef.current(), 0),
		[]
	);

	useEffect(() => {
		dispatch(registerGrid(name, primaryKey));
		if (!manual) {
			debouncedGetNewData();
		}
		return () => {
			if (!manual && !retainOnUnmount) {
				dispatch(unregisterGrid(name));
			}
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!manual && needsUpdate) {
			dispatch(clearRefresh(name));
			// Do not debounce refresh: debounce(0) can drop/coalesce updates and block search/filter refetches.
			getNewDataRef.current();
		}
	}, [needsUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

	const contextValue = useMemo(
		() => ({
			name,
			isLoading: trackRequestLifecycle ? isLoading : false,
			error: trackRequestLifecycle ? error : null,
			retry: trackRequestLifecycle ? retry : null,
		}),
		[name, trackRequestLifecycle, isLoading, error, retry]
	);

	return <GridContext.Provider value={contextValue}>{children}</GridContext.Provider>;
};

BaseGridProvider.propTypes = {
	name: PropTypes.string.isRequired,
	primaryKey: PropTypes.string,
	loadData: PropTypes.func,
	onDataReceived: PropTypes.func,
	manual: PropTypes.bool,
	retainOnUnmount: PropTypes.bool,
	/** When true, tracks in-flight fetch, ignores stale responses, and exposes error/retry. */
	trackRequestLifecycle: PropTypes.bool,
	children: PropTypes.node,
};

export const GridProvider = BaseGridProvider;

// Deprecated: use useGridContext() hook instead
export const consumer = (Component) => ({ children, ...props }) => {
	return (
		<GridContext.Consumer>
			{(context) => (
				<Component gridName={context.name} {...props}>
					{children}
				</Component>
			)}
		</GridContext.Consumer>
	);
};
